/**
 * Deterministic query engine (M5.md AC-3, AC-4, AC-5). Executes a structured
 * `QueryPlan` over the loaded point set and returns an exact, reproducible
 * answer plus a map effect. THE LLM NEVER RUNS THIS PATH — it only produces the
 * plan; every number here is computed locally, so answers are correct,
 * provider-independent, and cost $0 to recompute. Spatial primitives use
 * haversine (see `filter.ts`), matching an independent Turf.js control.
 */

import type { Point } from "@/lib/map/pointset";
import { haversineKm, selectPoints } from "./filter";
import type { AggregateFn, MapEffect, QueryPlan, QueryResult } from "./types";

function fmt(n: number): string {
  if (Number.isInteger(n)) return n.toLocaleString("en-US");
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function numericValues(points: Point[], column: string): number[] {
  const out: number[] = [];
  for (const p of points) {
    const n = Number(p.props[column]);
    if (Number.isFinite(n)) out.push(n);
  }
  return out;
}

/** Apply an aggregate function to a numeric series (count ignores the series). */
function aggregate(fn: AggregateFn, count: number, values: number[]): number {
  switch (fn) {
    case "count":
      return count;
    case "sum":
      return values.reduce((a, b) => a + b, 0);
    case "avg":
      return values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    case "min":
      return values.length ? Math.min(...values) : 0;
    case "max":
      return values.length ? Math.max(...values) : 0;
  }
}

const ids = (points: Point[]): string[] => points.map((p) => p.id);

/** Execute a query plan. Pure over `points`; independent of the LLM. */
export function runQuery(plan: QueryPlan, points: Point[]): QueryResult {
  const base = plan.filter ? selectPoints(points, plan.filter) : points;

  // ---- Spatial ----
  if (plan.spatial) {
    const { kind, anchor, km, n } = plan.spatial;
    if (kind === "within_radius") {
      const radius = km ?? 0;
      const within = base.filter((p) => haversineKm(p.lat, p.lng, anchor.lat, anchor.lng) <= radius);
      // A spatial filter can still feed an aggregate ("how many within 10mi").
      if (plan.aggregate) return scalarOrGrouped(plan, within, `within ${fmt(radius)} km of ${anchor.label}`);
      return {
        answerText: `${fmt(within.length)} ${within.length === 1 ? "location" : "locations"} within ${fmt(radius)} km of ${anchor.label}.`,
        effect: { kind: "highlight", ids: ids(within) },
        matchedIds: ids(within),
      };
    }
    if (kind === "nearest_n") {
      const count = n ?? 1;
      const ranked = [...base]
        .map((p) => ({ p, d: haversineKm(p.lat, p.lng, anchor.lat, anchor.lng) }))
        .sort((a, b) => a.d - b.d)
        .slice(0, count);
      const near = ranked.map((r) => r.p);
      return {
        answerText: `The ${near.length === 1 ? "nearest location" : `${fmt(near.length)} nearest locations`} to ${anchor.label}${ranked.length ? ` (closest ${fmt(ranked[0]!.d)} km away)` : ""}.`,
        effect: { kind: "fit", ids: ids(near) },
        matchedIds: ids(near),
      };
    }
    // distance_to: annotate each point with its distance to the anchor.
    const annotations = base.map((p) => ({
      id: p.id,
      text: `${fmt(haversineKm(p.lat, p.lng, anchor.lat, anchor.lng))} km`,
    }));
    return {
      answerText: `Distance from ${anchor.label} annotated for ${fmt(base.length)} ${base.length === 1 ? "location" : "locations"}.`,
      effect: { kind: "annotate", annotations },
      matchedIds: ids(base),
    };
  }

  // ---- Tabular aggregate / group-by ----
  if (plan.aggregate) return scalarOrGrouped(plan, base, plan.filter ? "matching your filter" : "");

  // ---- Top-N / sort ----
  if (plan.topN) {
    const { column, n, asc } = plan.topN;
    const ranked = [...base]
      .filter((p) => Number.isFinite(Number(p.props[column])))
      .sort((a, b) => (asc ? 1 : -1) * (Number(a.props[column]) - Number(b.props[column])))
      .slice(0, n);
    const dir = asc ? "bottom" : "top";
    return {
      answerText: `The ${dir} ${fmt(ranked.length)} by ${column}: ${ranked
        .slice(0, 5)
        .map((p) => fmt(Number(p.props[column])))
        .join(", ")}${ranked.length > 5 ? ", …" : ""}.`,
      effect: { kind: "highlight", ids: ids(ranked) },
      matchedIds: ids(ranked),
    };
  }

  // ---- Plain filter → highlight the matches ----
  return {
    answerText: `${fmt(base.length)} ${base.length === 1 ? "row matches" : "rows match"}.`,
    effect: { kind: "highlight", ids: ids(base) },
    matchedIds: ids(base),
  };
}

/** Compute a scalar or grouped aggregate over `points` and phrase the answer. */
function scalarOrGrouped(plan: QueryPlan, points: Point[], scopeNote: string): QueryResult {
  const agg = plan.aggregate!;
  const scope = scopeNote ? ` (${scopeNote})` : "";

  if (agg.groupBy) {
    const buckets = new Map<string, Point[]>();
    for (const p of points) {
      const key = p.props[agg.groupBy] ?? "—";
      let bucket = buckets.get(key);
      if (!bucket) buckets.set(key, (bucket = []));
      bucket.push(p);
    }
    const groups = [...buckets.entries()]
      .map(([group, groupPoints]) => ({
        group,
        value: aggregate(agg.fn, groupPoints.length, agg.column ? numericValues(groupPoints, agg.column) : []),
        count: groupPoints.length,
      }))
      .sort((a, b) => b.value - a.value);
    const label = agg.fn === "count" ? "count" : `${agg.fn} of ${agg.column}`;
    // On-map effect: one badge per group carrying the REAL aggregate, placed on
    // the group's first point so the annotation layer can label it (AC group-by
    // map effect). Empty groups can't be placed, so they're skipped.
    const annotations = groups
      .map((g) => {
        const anchor = buckets.get(g.group)?.[0];
        return anchor ? { id: anchor.id, text: `${g.group}: ${fmt(g.value)}` } : null;
      })
      .filter((a): a is { id: string; text: string } => a !== null);
    return {
      answerText: `${label} by ${agg.groupBy}${scope}: ${groups
        .slice(0, 6)
        .map((g) => `${g.group} ${fmt(g.value)}`)
        .join(", ")}${groups.length > 6 ? ", …" : ""}.`,
      effect: { kind: "annotate", annotations },
      matchedIds: ids(points),
      groups,
    };
  }

  const value = aggregate(agg.fn, points.length, agg.column ? numericValues(points, agg.column) : []);
  const label = agg.fn === "count" ? `${fmt(value)} ${value === 1 ? "row" : "rows"}` : `${agg.fn} of ${agg.column} is ${fmt(value)}`;
  return {
    answerText: `${label}${scope}.`,
    effect: { kind: "highlight", ids: ids(points) } as MapEffect,
    matchedIds: ids(points),
  };
}
